const LOGS = { VIDEO: false };
import { Media } from "./video";

export const DEFAULT_MIME = 'video/mp4; codecs="avc1.42E01E"';
export const DEFAULT_MODE = "segments";

export default class SourceBufferManager {
    readonly MIME_TYPE: string = DEFAULT_MIME;
    readonly APPEND_MODE: AppendMode = DEFAULT_MODE;
    private _videoElement: HTMLVideoElement;
    private _mediaSource: MediaSource;
    private _isSourceBufferInitialized: boolean = false;

    constructor(videoElement: HTMLVideoElement, mediaSource: MediaSource) {
        this._videoElement = videoElement;
        this._mediaSource = mediaSource;
    }

    get bufferExists() {
        return !!this._mediaSource.sourceBuffers && this._mediaSource.sourceBuffers.length !== 0;
    }

    get sourceBuffer() {
        if (!this.bufferExists) return null;
        return this._mediaSource.sourceBuffers[0];
    }

    get isSourceBufferReady() {
        return this._isSourceBufferInitialized && this.sourceBuffer && !this.sourceBuffer.updating;
    }

    get bufferIsEmpty() {
        return !this.sourceBuffer || this.sourceBuffer.buffered.length === 0;
    }

    private getBufferDuration() {
        if (!this.sourceBuffer) {
            console.error("Source buffer is undefined, returning 0 for the buffer duration.");
            return 0;
        }
        const endOfBuffer = this.sourceBuffer.buffered.length - 1;
        return this.sourceBuffer.buffered.end(endOfBuffer);
    }

    initSourceBuffer = () => {
        LOGS.VIDEO && console.log("Setting up source buffer...");

        if (this.bufferExists) {
            console.error("Cannot setup source buffer because it already exists.");
            return;
        }

        try {
            this._mediaSource.addSourceBuffer(this.MIME_TYPE);

            if (!this.sourceBuffer) {
                console.error("Somehow the source buffer is null after creating it");
                return;
            }

            this.sourceBuffer.mode = this.APPEND_MODE;

            this._isSourceBufferInitialized = true;
            LOGS.VIDEO && console.log("Source buffer is ready");
        } catch (err) {
            console.error("Failed to create SourceBuffer: ", err);
        }
    };

    private async _waitForSourceBufferReady() {
        LOGS.VIDEO && console.log("Waiting for source buffer to be ready...");
        while (!this.isSourceBufferReady) {
            LOGS.VIDEO && console.warn("Source buffer is not ready, waiting...");
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        LOGS.VIDEO && console.log("Source buffer is ready");
    }

    async playVideo(video: Media) {
        const playAfterAppend = () => {
            if (!this.isSourceBufferReady) {
                console.error("Cannot play the video after it has been appended");
                return;
            }
            LOGS.VIDEO && console.log("Playing new video data after appending");
            this._videoElement.currentTime = 0;
            this._mediaSource.duration = this.getBufferDuration();
            this._videoElement.play().catch(console.error);
            this.sourceBuffer!.removeEventListener("updateend", playAfterAppend);
        };

        LOGS.VIDEO && console.log("Attempting to play a video...");

        await this._waitForSourceBufferReady();

        if (!this.isSourceBufferReady) {
            console.warn("Source buffer is not ready after waiting for it to be ready.");
            return;
        }

        if (video.shouldPlayImmediately) {
            await this._clearBuffer();
            this.sourceBuffer!.addEventListener("updateend", playAfterAppend);
        }

        try {
            LOGS.VIDEO && console.log("Appending new video data");
            this.sourceBuffer!.appendBuffer(video.data);
        } catch (err) {
            console.error("Error appending video data: ", err);
        }
    }

    private _clearBuffer(): Promise<void> {
        LOGS.VIDEO && console.log("Attempting to clear the buffer...");
        return new Promise((resolve) => {
            if (!this.isSourceBufferReady) {
                LOGS.VIDEO && console.warn("Source buffer is not ready, cannot clear yet");
                resolve();
                return;
            }
            if (this.bufferIsEmpty) {
                resolve();
                return;
            }

            const bufferStart = this.sourceBuffer!.buffered.start(0);
            const bufferEnd = this.sourceBuffer!.buffered.end(
                this.sourceBuffer!.buffered.length - 1,
            );
            LOGS.VIDEO && console.log(`Clearing buffer from ${bufferStart} to ${bufferEnd}`);

            this.sourceBuffer!.remove(bufferStart, bufferEnd);

            const onBufferCleared = () => {
                if (!this.sourceBuffer!.updating) {
                    this.sourceBuffer!.removeEventListener("updateend", onBufferCleared);
                    resolve();
                }
            };
            this.sourceBuffer!.addEventListener("updateend", onBufferCleared);
        });
    }
}
